import React, { useState, useEffect, useCallback } from 'react';
import gateApi from '../api';
import { C, FormShell, Field, TextInput, Banner } from './FormKit';

// LOAD_TYPES mirrors ExitForm.jsx/EntryForm.jsx — these are the only material
// strings that ever show up as expectedLoad, so they're the only ones a price
// needs to exist for.
const LOAD_TYPES = ['10mm', '20mm', '30mm', '40mm', '60mm', 'DUST', 'BOULDER', 'WMM', 'GSB', 'BC-1-Dust', 'OTHER'];

export default function MaterialPrices() {
  const [prices, setPrices] = useState({});   // material (upper) -> price string
  const [saving, setSaving] = useState('');    // material currently saving
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await gateApi.getMaterialPrices();
      // Backend stores material.toUpperCase() (see MaterialPriceController) —
      // match rows back to LOAD_TYPES case-insensitively so the map is keyed
      // the same way the inputs below are (by the raw LOAD_TYPES casing).
      const map = {};
      rows.forEach(r => {
        const match = LOAD_TYPES.find(m => m.toUpperCase() === r.material.toUpperCase());
        if (match) map[match] = String(r.pricePerTonne);
      });
      setPrices(map);
    } catch {
      setResult({ ok: false, msg: 'Could not load prices.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(material) {
    const value = prices[material];
    const num = parseFloat(value);
    if (!num || num <= 0) {
      setResult({ ok: false, msg: `Enter a valid price for ${material}.` });
      return;
    }
    setSaving(material); setResult(null);
    try {
      await gateApi.setMaterialPrice(material, num);
      setResult({ ok: true, msg: `${material} price saved.` });
    } catch (e) {
      const d = e.response?.data;
      setResult({ ok: false, msg: d?.message || d?.error || `Could not save ${material}.` });
    } finally {
      setSaving('');
    }
  }

  return (
    <FormShell title="Material Prices" accent={C.teal}>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>Material prices</h1>
      <p style={{ color: C.muted, fontSize: 14, margin: '0 0 26px' }}>
        ₹ per tonne. Changing a price only affects challans generated after the change —
        challans already generated keep the rate they were priced at.
      </p>

      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      {loading ? (
        <div style={{ color: C.muted, fontSize: 14 }}>Loading…</div>
      ) : (
        LOAD_TYPES.map(material => (
          <div key={material} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <Field label={material}>
                <TextInput
                  value={prices[material] ?? ''}
                  onChange={v => setPrices(p => ({ ...p, [material]: v }))}
                  type="number" mono placeholder="₹ per tonne"
                />
              </Field>
            </div>
            <button
              onClick={() => save(material)}
              disabled={saving === material}
              style={{
                marginBottom: 20, padding: '14px 20px', borderRadius: 10, border: 'none',
                background: saving === material ? C.panel2 : C.teal,
                color: saving === material ? C.faint : '#fff',
                fontFamily: C.mono, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >{saving === material ? 'Saving…' : 'Save'}</button>
          </div>
        ))
      )}
    </FormShell>
  );
}
